import * as THREE from "three";
import type { ExtendedMesh } from "../three-extensions";

/**
 * Free the GPU resources associated with a single ephemeral three.js object.
 * @param obj Object to process
 */
export function freeThreeObject3D(obj: THREE.Object3D): void {
  if (isGeometryObject3D(obj)) {
    if (obj instanceof THREE.Mesh && typeof (obj as ExtendedMesh).dispose === "function") {
      if (!obj.userData.isCached) {
        (obj as ExtendedMesh).dispose();
      }
    }
    if (!obj.geometry.userData?.isCached) {
      obj.geometry.dispose();
    }
  }
  if (obj instanceof THREE.Light && obj.shadow) {
    obj.shadow.dispose();
  }
  if (obj.userData?.customDispose && typeof obj.userData.customDispose === "function") {
    // Free ephemeral resources such as textures in GPU memory associated with this object.
    obj.userData.customDispose();
  }
}

/**
 * A more robust check than checking the type of the object.
 */
export function isGeometryObject3D(obj: THREE.Object3D): obj is THREE.Object3D & { geometry: THREE.BufferGeometry } {
  return "geometry" in obj && obj.geometry instanceof THREE.BufferGeometry;
}

/**
 * Clear a three.js scene and free GPU memory associated with ephemeral scene geometry (without isCached userData flag).
 * @param scene Scene to clear.
 * @param excludeChild Direct child of the scene that's left in place, for example a reference grid.
 */
export function clearThreeScene(scene: THREE.Scene, excludeChild?: THREE.Object3D): void {
  for (const child of [...scene.children]) {
    if (child === excludeChild) {
      continue;
    }

    child.traverse(freeThreeObject3D);

    // Generally persistent objects like scene lighting should be added under the "staticContent" object.
    // Clear everything else with the exception of cameras.
    if (!(child instanceof THREE.Camera)) {
      scene.remove(child);
    }
  }
}
